import React, { useEffect, useLayoutEffect, useRef } from 'react';

import { css } from '@emotion/css';
import { IotDevicePhysicalEvent } from '@prisma/client';
import { formatISO9075 } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { m } from 'framer-motion';
import {
  createChart, ColorType, UTCTimestamp, IChartApi,
  LineStyle,
  AutoscaleInfo,
  BarPrice,
  Time,
  TickMarkType,
} from 'lightweight-charts';
import { observer } from 'mobx-react-lite';
import {
  useUnmount,
} from 'usehooks-ts';

import { ExtractPromiseSuccess } from '^/types/__ResultType';
import { useLifecycles } from '^/userWeb/hooks/useLifecycle';
import { borders } from '^/userWeb/styles/commonClasses';

import { SensorsDetailPopupViewModel } from './SensorsDetail.popup.viewmodel';

const initialData = [
  { time: '2018-12-22', value: 32.51 },
  { time: '2018-12-23', value: 31.11 },
  { time: '2018-12-24', value: 27.02 },
  { time: '2018-12-25', value: 27.32 },
  { time: '2018-12-26', value: 25.17 },
  { time: '2018-12-27', value: 28.89 },
  { time: '2018-12-28', value: 25.46 },
  { time: '2018-12-29', value: 23.92 },
  { time: '2018-12-30', value: 22.68 },
  { time: '2018-12-31', value: 22.67 },
];

export const TempHumidityChart = observer(function TempHumidityChart(
  props: {
    viewmodel: SensorsDetailPopupViewModel,
    data: ExtractPromiseSuccess<
      SensorsDetailPopupViewModel['sensorData']
    >;
    colors?: {
      backgroundColor?: string;
      lineColor?: string;
      textColor?: string;
      areaTopColor?: string;
      areaBottomColor?: string;
    };
    dimension?: {
      width: number;
      height: number;
    };

  }) {
  const {
    data,
    dimension,
    viewmodel,
    colors: {
      backgroundColor = 'white',
      lineColor = '#2962FF',
      textColor = 'black',
      areaTopColor = '#2962FF',
      areaBottomColor = 'rgba(41, 98, 255, 0.28)',
    } = {
      data: initialData,
    },
  } = props;

  const dataWithDateFixed = data
    .map((d) => {
      const utctime = d.assumedTime.getTime() / 1000 as UTCTimestamp;
      const time = utctime + (3600 * 9) as UTCTimestamp; // UTC to KST: +9
      return {
        ...d,
        time,
      };
    })
    .reverse();

  const chartContainerRef = useRef<
    HTMLDivElement
  >(null);
  const chart = useRef<
    ReturnType<typeof createChart> | null
  >(null);
  // let chart: ReturnType<typeof createChart>
  const handleResize = () => {
    chart.current?.applyOptions({
      width: chartContainerRef.current?.clientWidth,
    });
  };
  const temperatureSeriesRef = useRef<
    ReturnType<IChartApi['addLineSeries']> | null
  >(null);

  const humiditySeriesRef = useRef<
    ReturnType<IChartApi['addHistogramSeries']> | null
  >(null);

  useLifecycles(
    () => {
      if (!chartContainerRef.current) {
        return;
      }
      const chartContainer = chartContainerRef.current;
      const chartCurrent = createChart(chartContainerRef.current, {
        layout: {
          background: { type: ColorType.Solid, color: backgroundColor },
          textColor,
        },
        autoSize: true,
        width: chartContainer.clientWidth,
        height: chartContainer.clientHeight,
        handleScale: {
          axisPressedMouseMove: false,
        },
        handleScroll: {
          vertTouchDrag: false,
        },
        rightPriceScale: {
          visible: true,
          ticksVisible: true,
        },
        leftPriceScale: {
          visible: true,
        },
        localization: {
          // timeFormatter(t: HorzScaleItem) {

          // }
          dateFormat: 'yyyy.MM.dd',
        },

        timeScale: {
          // tickMarkFormatter(
          //   time: Time,
          //   tickMarkType: TickMarkType,
          //   locale: string,
          // ) {
          //   return '123';
          // },

          ticksVisible: true,
          timeVisible: true,
        },
        
      });
      chart.current = chartCurrent;

      chart.current.timeScale().fitContent();

      const temperatureSeries = chart
        .current
        .addLineSeries({
          // lineColor,
          color: 'orange',
          lineWidth: 1,
          // title: '온도',
          priceFormat: {
            formatter: (v: BarPrice) => {
              return `${v}°C`;
            },
          },
          
          // pointMarkersRadius: 2,
          autoscaleInfoProvider: () => {
            return {
              priceRange: {
                maxValue: 30,
                minValue: 0,
              },
            } as AutoscaleInfo;
          },
          // topColor: areaTopColor,
          // bottomColor: areaBottomColor,
        });
      temperatureSeriesRef.current = temperatureSeries;
      temperatureSeries.setData(dataWithDateFixed
        .map((d) => {
          return {
            time: d.time,
            value: d.temperature,
          };
        }).toArray(),
      );

      const humiditySeries = chart
        .current
        .addHistogramSeries({
          priceScaleId: 'left',
          color: '#0e94a8',
          // priceLineColor: 'red',
          
          
          // lineStyle: LineStyle.Dotted,
          // lineWidth: 1,
          // borderVisible: false,
          // wickUpColor: '#26a69a',
          // wickDownColor: '#ef5350',
          autoscaleInfoProvider: () => {
            return {
              priceRange: {
                maxValue: 80,
                minValue: 10,
              },
            } as AutoscaleInfo;
          },
        });
      humiditySeriesRef.current = humiditySeries;
      humiditySeries.setData(dataWithDateFixed.map((d) => {
        return {
          time: d.time,
          value: d.humidity,
        };
      }).toArray());

      chartCurrent.subscribeDblClick(
        (e) => {
          chartCurrent.setCrosshairPosition(
            0,
            e.time || (Date.now() / 1000) as UTCTimestamp,
            temperatureSeries,
          )
        }
      )
      chartCurrent.subscribeCrosshairMove(e => {
        // TODO: table scroll-to
      })

      window.addEventListener('resize', handleResize);
    },
    () => {
      window.removeEventListener(
        'resize', handleResize,
      );
      chart.current?.remove();
    },
  );
  // useLayoutEffect(() => {
  //   if (chart.current && dimension) {
  //     chart.current.applyOptions({
  //       width: dimension.width,
  //       height: dimension.height,
  //     });
  //     chart.current.autoSizeActive
  //   }
  // }, [dimension]);

  useEffect(() => {
    humiditySeriesRef.current?.setData(dataWithDateFixed.map((d) => {
      return {
        time: d.time,
        value: d.humidity,
      };
    }).toArray());
    temperatureSeriesRef.current?.setData(dataWithDateFixed
      .map((d) => {
        return {
          time: d.time,
          value: d.temperature,
        };
      }).toArray(),
    );
  }, [
    dataWithDateFixed,
  ]);

  return (
    <m.div
      layout="position"
      className={[
        css`
          width: 100%;
          height: 100%;
        `,
        borders.DEBUG_blueBorderDashed,
      ].join(' ')}
      ref={chartContainerRef}
    />
  );
});
